import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, Users, FolderOpen, FlaskConical, Radio, Search, Copy, Eye, EyeOff, Database, FileText, Link2, KeyRound, RotateCcw, Camera, Loader2, Network } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const labelCls = "font-mono text-[8px] tracking-[0.2em] text-zinc-600 uppercase";
const THREAT = { low: "text-zinc-400 border-white/15", medium: "text-nc-orange border-nc-orange/40", high: "text-red-400 border-red-500/40", critical: "text-red-300 border-red-400 bg-red-500/10" };
const CASE_BADGE = { open: "text-nc-blue border-nc-blue/40", active: "text-nc-orange border-nc-orange/40 bg-nc-orange/5", closed: "text-nc-green border-nc-green/40", cold: "text-zinc-500 border-white/15" };

export default function AdminNcDb() {
  const [overview, setOverview] = useState(null);
  const [config, setConfig] = useState(null);
  const [tab, setTab] = useState("npcs");
  const [npcs, setNpcs] = useState([]);
  const [cases, setCases] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [profile, setProfile] = useState(null);
  const [profileTab, setProfileTab] = useState("overview");
  const [caseFile, setCaseFile] = useState(null);
  const [keyShown, setKeyShown] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState({ difficulty: 3, npc_id: "" });
  const [genBusy, setGenBusy] = useState(false);
  const [mugBusy, setMugBusy] = useState("");
  const [fillBusy, setFillBusy] = useState(false);
  const [ivOpen, setIvOpen] = useState("");
  const [srv, setSrv] = useState("");
  const [servers, setServers] = useState([]);
  const [serverRows, setServerRows] = useState([]);
  const [newSrv, setNewSrv] = useState({ name: "", kind: "community" });
  const [srvBusy, setSrvBusy] = useState("");
  const [ownerFor, setOwnerFor] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");

  const sp = (url) => (srv ? `${url}${url.includes("?") ? "&" : "?"}server_id=${srv}` : url);
  const sParams = (extra) => ({ params: { ...(extra || {}), ...(srv ? { server_id: srv } : {}) } });

  const mugshotUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/ncdb/npc/${id}/mugshot.png`;

  const fillMugshots = async () => {
    setFillBusy(true);
    try {
      const r = await api.post(sp("/admin/ncdb/mugshots/missing?limit=10"), {}, { timeout: 600000 });
      toast.success(r.data.message);
      loadNpcs();
      if (profile) openProfile(profile.npc.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setFillBusy(false);
    }
  };

  const generateMugshot = async (npcId) => {
    setMugBusy(npcId);
    try {
      const r = await api.post(sp(`/admin/ncdb/npcs/${npcId}/mugshot`), {}, { timeout: 180000 });
      toast.success(r.data.message);
      openProfile(npcId);
      loadNpcs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setMugBusy("");
    }
  };

  const generateCase = async () => {
    setGenBusy(true);
    try {
      const r = await api.post(sp("/ncdb/cases/generate"), gen);
      toast.success(r.data.message);
      const list = await api.get("/ncdb/cases", sParams());
      setCases(list.data);
      openCase(r.data.case_number);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenBusy(false);
    }
  };

  const load = useCallback(() => {
    api.get("/ncdb/overview", sParams()).then((r) => setOverview(r.data)).catch(() => {});
    api.get("/admin/ncdb/config", sParams()).then((r) => {
      setConfig(r.data);
      setServers(r.data.servers || []);
      if (!srv && r.data.selected_server) setSrv(r.data.selected_server);
    }).catch(() => {});
    api.get("/admin/ncdb/servers").then((r) => setServerRows(r.data)).catch(() => {});
  }, [srv]);

  const loadNpcs = useCallback(() => {
    api.get("/ncdb/npcs", sParams({ q, category: cat })).then((r) => setNpcs(r.data)).catch(() => {});
  }, [q, cat, srv]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadNpcs(); }, [loadNpcs]);
  useEffect(() => { api.get("/ncdb/cases", sParams()).then((r) => setCases(r.data)).catch(() => {}); }, [srv]);

  const openProfile = (id) => {
    setProfileTab("overview");
    api.get(`/ncdb/npc/profile/${id}`, sParams()).then((r) => setProfile(r.data)).catch(() => toast.error("Profile failed to load"));
  };

  const openCase = (num) => {
    api.get(`/ncdb/case/${num}`, sParams()).then((r) => setCaseFile(r.data)).catch(() => toast.error("Case file failed to load"));
  };

  const seed = async () => {
    setBusy(true);
    try {
      const r = await api.post(sp("/admin/ncdb/seed-demo"));
      toast.success(r.data.message);
      load(); loadNpcs();
      api.get("/ncdb/cases", sParams()).then((r2) => setCases(r2.data));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const rotate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/ncdb/servers/${srv}/rotate-key`);
      setConfig((c) => ({ ...c, game_key: r.data.game_key }));
      toast.success("Key rotated — update the server bridge config");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const createServer = async () => {
    setSrvBusy("create");
    try {
      const r = await api.post("/admin/ncdb/servers", newSrv);
      toast.success(r.data.message);
      setNewSrv({ name: "", kind: "community" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSrvBusy("");
    }
  };

  const assignOwner = async (id) => {
    setSrvBusy(`owner-${id}`);
    try {
      const r = await api.post(`/admin/ncdb/servers/${id}/assign-owner`, { email: ownerEmail });
      toast.success(r.data.message);
      setOwnerFor("");
      setOwnerEmail("");
      const rows = await api.get("/admin/ncdb/servers");
      setServerRows(rows.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSrvBusy(""); }
  };

  const toggleServer = async (id) => {
    setSrvBusy(id);
    try {
      const r = await api.post(`/admin/ncdb/servers/${id}/toggle`);
      toast.success(r.data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSrvBusy("");
    }
  };

  const cloneRegistry = async (id) => {
    setSrvBusy(`clone-${id}`);
    try {
      const r = await api.post(`/admin/ncdb/servers/${id}/clone-registry`);
      toast.success(r.data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSrvBusy("");
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
        toast.success(`${label} copied`);
      } catch {
        toast.error("Copy blocked by the browser — select the key text and copy it manually");
      }
    }
  };

  if (!overview || !config) return <p className="font-mono text-[10px] text-zinc-600 animate-pulse p-4">Connecting to NCDB…</p>;

  return (
    <section data-testid="admin-ncdb-section" className="flex flex-col gap-4">
      <div className="border border-white/10 bg-black/40">
        <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
              <Fingerprint className="w-4 h-4 text-nc-blue" /> New Citizen Database — NPC · Crime · Case · Forensics
            </h2>
            <p className="font-mono text-[10px] text-zinc-500 mt-1.5">The persistent world registry. The UE5 bridge reads and writes this live via the game API; this panel is your window into it.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="ncdb-server-switch" value={srv} onChange={(e) => { setSrv(e.target.value); setProfile(null); setCaseFile(null); }} className="bg-[#050505] border border-nc-orange/50 text-nc-orange font-mono text-[10px] uppercase px-3 py-2 focus:outline-none">
              {servers.map((s) => <option key={s.id} value={s.id}>{s.name}{s.is_default ? " ★" : ""}{s.status === "disabled" ? " (disabled)" : ""}</option>)}
            </select>
            <button data-testid="ncdb-seed-demo" disabled={busy || overview.total_npcs > 0 || !(servers.find((s) => s.id === srv) || {}).is_default} onClick={seed} className="font-mono text-[9px] uppercase px-3 py-2 border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 transition-colors disabled:opacity-40 flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5" /> {overview.total_npcs > 0 ? "Registry Populated" : "Load Demo Dataset"}
            </button>
          </div>
        </div>

        {/* Server worlds manager */}
        <div data-testid="ncdb-server-manager" className="border-b border-white/10 px-6 py-4 flex flex-col gap-3">
          <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase">Server Worlds — one isolated registry per dedicated server</p>
          {serverRows.map((s) => (
            <div key={s.id} data-testid={`ncdb-server-row-${s.id}`} className={`border px-4 py-3 flex items-center gap-3 flex-wrap ${s.id === srv ? "border-nc-orange/50 bg-nc-orange/5" : "border-white/10"}`}>
              <button onClick={() => { setSrv(s.id); setProfile(null); setCaseFile(null); }} className="font-sans font-semibold text-sm text-zinc-100 hover:text-nc-orange transition-colors text-left">{s.name}</button>
              <span className={`font-mono text-[8px] uppercase border px-1.5 py-0.5 ${s.kind === "official" ? "text-nc-orange border-nc-orange/40" : "text-nc-blue border-nc-blue/40"}`}>{s.kind}</span>
              {s.is_default && <span className="font-mono text-[8px] uppercase text-zinc-500">default</span>}
              {s.owner_verified && <span className="font-mono text-[8px] uppercase text-nc-green border border-nc-green/40 px-1.5 py-0.5">verified owner</span>}
              <span className={`font-mono text-[8px] uppercase ${s.status === "disabled" ? "text-red-400" : "text-nc-green"}`}>{s.status}</span>
              <span className="font-mono text-[9px] text-zinc-500">{s.npc_count} NPCs · {s.case_count} cases</span>
              {s.owner_name && <span className="font-mono text-[8px] uppercase text-zinc-500">owner: {s.owner_name}</span>}
              <div className="ml-auto flex gap-1.5">
                <button data-testid={`ncdb-server-owner-${s.id}`} onClick={() => { setOwnerFor(ownerFor === s.id ? "" : s.id); setOwnerEmail(""); }} className="font-mono text-[8px] uppercase border border-nc-orange/40 text-nc-orange hover:bg-nc-orange/10 px-2 py-1.5 transition-colors">Assign owner</button>
                <button data-testid={`ncdb-server-clone-${s.id}`} onClick={() => cloneRegistry(s.id)} disabled={srvBusy === `clone-${s.id}` || s.id === "SRV-OFFICIAL-1" || s.npc_count > 0} title="Copy the official NPC registry into this empty world" className="font-mono text-[8px] uppercase border border-white/15 text-zinc-400 hover:border-nc-blue hover:text-nc-blue px-2 py-1.5 transition-colors disabled:opacity-30">Clone Official Registry</button>
                <button data-testid={`ncdb-server-toggle-${s.id}`} onClick={() => toggleServer(s.id)} disabled={s.is_default || srvBusy === s.id} className={`font-mono text-[8px] uppercase border px-2 py-1.5 transition-colors disabled:opacity-30 ${s.status === "disabled" ? "border-nc-green/40 text-nc-green hover:bg-nc-green/10" : "border-red-500/40 text-red-400 hover:bg-red-500/10"}`}>{s.status === "disabled" ? "Enable" : "Disable"}</button>
              </div>
              {ownerFor === s.id && (
                <div className="w-full flex items-end gap-2 border-t border-white/5 pt-2.5 mt-1">
                  <div className="flex-1 max-w-sm">
                    <label className={labelCls}>New owner's hub account email</label>
                    <input data-testid={`ncdb-owner-email-${s.id}`} value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="owner@theirserver.gg" className="w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-[11px] text-zinc-100 focus:outline-none focus:ring-1 focus:ring-nc-orange" />
                  </div>
                  <button data-testid={`ncdb-owner-save-${s.id}`} onClick={() => assignOwner(s.id)} disabled={srvBusy === `owner-${s.id}` || !ownerEmail.includes("@")} className="font-mono text-[9px] uppercase px-4 py-2 bg-nc-orange hover:bg-nc-orange/80 text-black font-semibold tracking-[0.2em] transition-colors disabled:opacity-40 flex items-center gap-1.5">
                    {srvBusy === `owner-${s.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />} Assign
                  </button>
                  <p className="font-mono text-[8px] text-zinc-600 w-full">Skips the claim-token flow and marks them verified instantly — the deck opens for them right away.</p>
                </div>
              )}
            </div>
          ))}
          <div className="flex items-end gap-3 flex-wrap border-t border-white/5 pt-3">
            <div>
              <label className={labelCls}>New server world</label>
              <input data-testid="ncdb-server-name" value={newSrv.name} onChange={(e) => setNewSrv((v) => ({ ...v, name: e.target.value }))} placeholder="e.g. Riverside RP — Community" maxLength={60} className="bg-[#050505] border border-white/10 px-3 py-2 font-mono text-[11px] text-zinc-100 w-64 focus:outline-none focus:ring-1 focus:ring-nc-orange" />
            </div>
            <select data-testid="ncdb-server-kind" value={newSrv.kind} onChange={(e) => setNewSrv((v) => ({ ...v, kind: e.target.value }))} className="bg-[#050505] border border-white/10 px-2 py-2 font-mono text-[11px] text-zinc-100">
              <option value="community">Community</option>
              <option value="official">Official</option>
            </select>
            <button data-testid="ncdb-server-create" onClick={createServer} disabled={srvBusy === "create" || newSrv.name.trim().length < 3} className="font-mono text-[9px] uppercase px-4 py-2.5 bg-nc-orange hover:bg-nc-orange/80 text-black font-semibold tracking-[0.2em] transition-colors disabled:opacity-40 flex items-center gap-2">
              {srvBusy === "create" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Issue Server + API Key
            </button>
            <p className="font-mono text-[9px] text-zinc-600 w-full">Creates an isolated world (NPCs, cases, interviews, player progress) with its own X-NC-Key to hand to the server owner. New worlds stay unlisted until the owner verifies via the public claim flow.</p>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 border-b border-white/10">
          {[[Users, "NPCs", overview.total_npcs, "ncdb-stat-npcs"], [FolderOpen, "Active Cases", overview.active_cases, "ncdb-stat-cases"], [FlaskConical, "Evidence Items", overview.evidence_items, "ncdb-stat-evidence"], [FileText, "Intel Notes", overview.intel_notes, "ncdb-stat-intel"], [Radio, "Events · 24h", overview.activity_24h, "ncdb-stat-activity"]].map(([Icon, label, v, tid]) => (
            <div key={label} data-testid={tid} className="px-6 py-4 border-r border-white/5 last:border-r-0">
              <p className={labelCls}><Icon className="w-3 h-3 inline mr-1 text-nc-blue" />{label}</p>
              <p className="font-sans font-bold text-2xl text-zinc-50 mt-1">{v}</p>
            </div>
          ))}
        </div>

        {/* Game API connection card */}
        <div className="px-6 py-4 border-b border-white/10 flex flex-col gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <KeyRound className="w-3.5 h-3.5 text-nc-orange" />
            <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase">UE5 Game API — connect the bridge with these</p>
            <span className="font-mono text-[8px] text-zinc-600 uppercase">auth header: X-NC-Key</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <code data-testid="ncdb-api-key" className="font-mono text-[11px] text-zinc-300 bg-[#050505] border border-white/10 px-3 py-2 break-all">
              {keyShown ? config.game_key : "••••••••••••••••••••••••••••••••"}
            </code>
            <button data-testid="ncdb-key-reveal" onClick={() => setKeyShown((s) => !s)} aria-label={keyShown ? "Hide game API key" : "Reveal game API key"} className="border border-white/15 p-2 text-zinc-400 hover:text-white transition-colors">
              {keyShown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
            <button data-testid="ncdb-key-copy" onClick={() => copy(config.game_key, "Game key")} className="border border-white/15 p-2 text-zinc-400 hover:text-white transition-colors" aria-label="Copy game API key"><Copy className="w-3.5 h-3.5" /></button>
            <button data-testid="ncdb-key-rotate" onClick={rotate} disabled={busy} className="font-mono text-[9px] uppercase px-2.5 py-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Rotate</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
            {config.endpoints.map((e) => (
              <p key={e.path} className="font-mono text-[10px] text-zinc-400 py-0.5">
                <span className={e.method === "GET" ? "text-nc-green" : "text-nc-orange"}>{e.method}</span>{" "}
                <button onClick={() => copy(`${process.env.REACT_APP_BACKEND_URL}${e.path}`, "Endpoint")} className="text-zinc-300 hover:text-nc-blue transition-colors">{e.path}</button>
                <span className="text-zinc-600"> — {e.use}</span>
              </p>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 flex gap-2">
          {[["npcs", "NPC Database"], ["cases", "Case Files"]].map(([t, label]) => (
            <button key={t} data-testid={`ncdb-tab-${t}`} onClick={() => setTab(t)} className={`font-mono text-[10px] uppercase px-3 py-2 border transition-colors ${tab === t ? "border-nc-blue text-nc-blue bg-nc-blue/5" : "border-white/10 text-zinc-500 hover:border-white/30"}`}>{label}</button>
          ))}
        </div>

        {tab === "npcs" && (
          <div className="p-6 flex flex-col gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input data-testid="ncdb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, alias, occupation…" className="bg-[#050505] border border-white/10 pl-9 pr-3 py-2 font-mono text-sm text-zinc-100 w-72 focus:outline-none focus:ring-1 focus:ring-nc-blue" />
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {["", ...Object.keys(overview.categories)].map((c) => (
                  <button key={c || "all"} data-testid={`ncdb-cat-${c || "all"}`} onClick={() => setCat(c)} className={`font-mono text-[9px] uppercase px-2.5 py-1.5 border transition-colors ${cat === c ? "border-nc-blue text-nc-blue" : "border-white/10 text-zinc-500 hover:border-white/30"}`}>
                    {c ? c.replace(/_/g, " ") : "All"} {c ? `(${overview.categories[c]})` : ""}
                  </button>
                ))}
              </div>
              <button data-testid="ncdb-mugshots-fill-all" onClick={fillMugshots} disabled={fillBusy || npcs.every((n) => n.mugshot)} className="font-mono text-[9px] uppercase px-3 py-2 border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 transition-colors disabled:opacity-40 flex items-center gap-1.5 ml-auto">
                {fillBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                {fillBusy ? "Generating portraits…" : `Fill Missing Mugshots (${npcs.filter((n) => !n.mugshot).length})`}
              </button>
            </div>
            <div className="border border-white/10">
              {npcs.length === 0 && <p data-testid="ncdb-npcs-empty" className="font-mono text-[10px] text-zinc-600 p-4">No NPCs registered yet — load the demo dataset or push registrations from the game.</p>}
              {npcs.map((n, i) => (
                <button key={n.id} data-testid={`ncdb-npc-row-${i + 1}`} onClick={() => openProfile(n.id)} className="w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors flex items-center gap-4 flex-wrap">
                  {n.mugshot ? (
                    <img src={mugshotUrl(n.id)} alt={`${n.name} registry portrait`} className="w-8 h-8 object-cover border border-white/15 shrink-0" />
                  ) : (
                    <span className="w-8 h-8 border border-white/10 bg-white/5 flex items-center justify-center font-mono text-[9px] text-zinc-500 shrink-0">{n.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
                  )}
                  <span className="font-mono text-[10px] text-zinc-600 w-24">{n.id}</span>
                  <span className="font-sans font-semibold text-sm text-zinc-100">{n.name}{(n.aliases || []).length > 0 && <span className="text-zinc-500 font-normal"> "{n.aliases[0]}"</span>}</span>
                  <span className="font-mono text-[9px] text-zinc-500 uppercase">{n.category?.replace(/_/g, " ")}</span>
                  <span className="font-mono text-[10px] text-zinc-400 hidden md:inline">{n.occupation}</span>
                  <span className={`ml-auto font-mono text-[9px] uppercase border px-2 py-0.5 ${THREAT[n.threat_level] || THREAT.low}`}>{n.threat_level}</span>
                </button>
              ))}
            </div>

            {/* Profile drawer */}
            {profile && (
              <div data-testid="ncdb-npc-detail" className="border border-nc-blue/40 bg-[#050505]/70 p-5 flex flex-col gap-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-4">
                    {profile.npc.mugshot ? (
                      <img data-testid="ncdb-mugshot" src={mugshotUrl(profile.npc.id)} alt={`${profile.npc.name} registry portrait`} className="w-24 h-28 object-cover border border-white/15" />
                    ) : (
                      <button data-testid="ncdb-mugshot-generate" onClick={() => generateMugshot(profile.npc.id)} disabled={mugBusy === profile.npc.id} className="w-24 h-28 border border-dashed border-white/20 bg-white/5 flex flex-col items-center justify-center gap-1.5 text-zinc-500 hover:border-nc-blue hover:text-nc-blue transition-colors disabled:opacity-50">
                        <Camera className="w-4 h-4" />
                        <span className="font-mono text-[8px] uppercase tracking-[0.15em] px-1 text-center">{mugBusy === profile.npc.id ? "Generating…" : "Generate Mugshot"}</span>
                      </button>
                    )}
                    <div>
                      <p className="font-sans font-bold text-xl text-zinc-50">{profile.npc.name} {(profile.npc.aliases || []).length > 0 && <span className="text-nc-blue">"{profile.npc.aliases.join('" · "')}"</span>}</p>
                      <p className="font-mono text-[10px] text-zinc-500 mt-1">{profile.npc.id} · {profile.npc.occupation} · {(profile.npc.organizations || []).join(", ")}</p>
                    </div>
                  </div>
                  <button data-testid="ncdb-detail-close" onClick={() => setProfile(null)} className="font-mono text-[9px] uppercase px-2.5 py-1.5 border border-white/15 text-zinc-400 hover:border-white/40 transition-colors">Close</button>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {[["overview", "Overview"], ["history", "Criminal History"], ["relationships", "Relationships"], ["cases", `Cases (${profile.linked_cases.length})`], ["evidence", `Evidence (${profile.evidence.length})`], ["intel", `Intel (${profile.notes.length})`]].map(([t, label]) => (
                    <button key={t} data-testid={`ncdb-ptab-${t}`} onClick={() => setProfileTab(t)} className={`font-mono text-[9px] uppercase px-2.5 py-1.5 border transition-colors ${profileTab === t ? "border-nc-orange text-nc-orange" : "border-white/10 text-zinc-500"}`}>{label}</button>
                  ))}
                </div>

                {profileTab === "overview" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className={`${labelCls} mb-2`}>Biography</p>
                      <p className="font-mono text-[11px] text-zinc-300 leading-relaxed">{profile.npc.biography || "—"}</p>
                      <p className={`${labelCls} mt-4 mb-2`}>Motivations</p>
                      {(profile.npc.motivations || []).map((m) => <p key={m} className="font-mono text-[10px] text-zinc-400">· {m}</p>)}
                      <p className={`${labelCls} mt-3 mb-2`}>Fears</p>
                      {(profile.npc.fears || []).map((f) => <p key={f} className="font-mono text-[10px] text-zinc-400">· {f}</p>)}
                    </div>
                    <div>
                      <p className={`${labelCls} mb-2`}>Biometrics</p>
                      {Object.entries(profile.npc.biometrics || {}).map(([k, v]) => (
                        <div key={k} className="flex justify-between border-b border-white/5 py-1"><span className="font-mono text-[9px] text-zinc-600 uppercase">{k}</span><span className="font-mono text-[10px] text-zinc-300">{v}</span></div>
                      ))}
                      <p className={`${labelCls} mt-4 mb-2`}>Tags</p>
                      <div className="flex gap-1.5 flex-wrap">{(profile.npc.tags || []).map((t) => <span key={t} className="font-mono text-[8px] uppercase border border-white/15 text-zinc-400 px-1.5 py-0.5">{t}</span>)}</div>
                      <p className={`${labelCls} mt-4 mb-2`}>Recent Activity</p>
                      {profile.recent_activity.slice(0, 4).map((a) => <p key={a.id} className="font-mono text-[10px] text-zinc-400 py-0.5">▸ {a.summary}</p>)}
                    </div>
                  </div>
                )}

                {profileTab === "history" && (
                  <div data-testid="ncdb-history" className="flex flex-col gap-0">
                    {profile.criminal_history.length === 0 && <p className="font-mono text-[10px] text-zinc-600">Clean record.</p>}
                    {profile.criminal_history.map((r) => (
                      <div key={r.id} className="flex items-center gap-3 border-l-2 border-nc-blue/40 pl-3 py-1.5">
                        <span className="font-mono text-[10px] text-zinc-500 w-14">{r.date}</span>
                        <span className="font-mono text-[9px] uppercase text-nc-orange w-20">{r.type}</span>
                        <span className="font-mono text-[11px] text-zinc-300">{r.offense || r.notes}</span>
                      </div>
                    ))}
                  </div>
                )}

                {profileTab === "relationships" && (
                  <div data-testid="ncdb-relationships" className="flex flex-col gap-2">
                    {profile.relationships.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No known associates.</p>}
                    {profile.relationships.map((r) => (
                      <div key={r.id} className="flex items-center gap-3">
                        <span className="font-sans font-semibold text-xs text-zinc-100 w-40 truncate">{r.target_name}</span>
                        <span className="font-mono text-[9px] text-zinc-500 flex-1 truncate">{r.relationship}</span>
                        <div className="w-28 h-1.5 bg-white/10"><div className="h-full bg-nc-blue" style={{ width: `${r.strength}%` }} /></div>
                        <span className="font-mono text-[9px] text-zinc-400 w-8 text-right">{r.strength}%</span>
                      </div>
                    ))}
                  </div>
                )}

                {profileTab === "cases" && (
                  <div className="flex gap-2 flex-wrap">
                    {profile.linked_cases.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No linked cases.</p>}
                    {profile.linked_cases.map((c) => (
                      <button key={c.id} data-testid={`ncdb-link-${c.case_number}`} onClick={() => { setTab("cases"); openCase(c.case_number); }} className={`font-mono text-[10px] border px-3 py-2 hover:bg-white/5 transition-colors ${CASE_BADGE[c.status] || CASE_BADGE.open}`}>
                        {c.case_number} — {c.title}
                      </button>
                    ))}
                  </div>
                )}

                {profileTab === "evidence" && (
                  <div className="flex flex-col gap-2">
                    {profile.evidence.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No forensic evidence on file.</p>}
                    {profile.evidence.map((e) => (
                      <div key={e.id} className="border border-white/10 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-[9px] uppercase text-nc-blue">{e.type}</span>
                          {e.match_confidence != null && <span className="font-mono text-[10px] text-nc-green">{e.match_subject} — {e.match_confidence}%</span>}
                        </div>
                        <p className="font-mono text-[10px] text-zinc-400 mt-1">{e.description}</p>
                      </div>
                    ))}
                  </div>
                )}

                {profileTab === "intel" && (
                  <div className="flex flex-col gap-2">
                    {profile.notes.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No intel notes.</p>}
                    {profile.notes.map((n) => (
                      <div key={n.id} className="border border-white/10 p-3">
                        <p className="font-mono text-[9px] text-nc-orange">{n.ref} · {n.analyst} · {String(n.created_at).slice(0, 10)}</p>
                        <p className="font-mono text-[10px] text-zinc-300 mt-1 leading-relaxed">{n.body}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === "cases" && (
          <div className="p-6 flex flex-col gap-4">
            <div data-testid="ncdb-casegen" className="border border-nc-blue/30 bg-nc-blue/5 p-4 flex items-end gap-3 flex-wrap">
              <div>
                <label className={labelCls}>Difficulty</label>
                <select data-testid="ncdb-casegen-difficulty" value={gen.difficulty} onChange={(e) => setGen((g) => ({ ...g, difficulty: parseInt(e.target.value, 10) }))} className="bg-[#050505] border border-white/10 px-2 py-2 font-mono text-[11px] text-zinc-100">
                  {[1, 2, 3, 4, 5].map((d) => <option key={d} value={d}>{`${d} — ${["", "street-level", "standard", "involved", "complex", "legendary"][d]}`}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Anchor suspect (optional)</label>
                <select data-testid="ncdb-casegen-npc" value={gen.npc_id} onChange={(e) => setGen((g) => ({ ...g, npc_id: e.target.value }))} className="bg-[#050505] border border-white/10 px-2 py-2 font-mono text-[11px] text-zinc-100 max-w-56">
                  <option value="">Auto-pick from network</option>
                  {npcs.filter((n) => n.category === "criminal").map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
                </select>
              </div>
              <button data-testid="ncdb-casegen-run" onClick={generateCase} disabled={genBusy} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 flex items-center gap-2 transition-colors">
                {genBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Network className="w-3.5 h-3.5" />} Generate Case
              </button>
              <p className="font-mono text-[9px] text-zinc-500 w-full">Assembles suspects, witnesses, evidence and red herrings from the live NPC network — ready for in-game pickup.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {cases.length === 0 && <p data-testid="ncdb-cases-empty" className="font-mono text-[10px] text-zinc-600">No cases opened yet.</p>}
              {cases.map((c, i) => (
                <button key={c.id} data-testid={`ncdb-case-card-${i + 1}`} onClick={() => setCaseFile(null) || openCase(c.case_number)} className="text-left border border-white/10 bg-[#050505]/50 p-4 hover:border-nc-blue/40 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] text-nc-blue">{c.case_number}</span>
                    <span className={`font-mono text-[9px] uppercase border px-2 py-0.5 ${CASE_BADGE[c.status]}`}>{c.status}</span>
                  </div>
                  <p className="font-sans font-semibold text-sm text-zinc-100 mt-1.5">{c.title}</p>
                  <p className="font-mono text-[9px] text-zinc-500 mt-1">{c.type} · {c.lead_detective} · {(c.linked_npcs || []).length} linked NPCs</p>
                </button>
              ))}
            </div>

            {caseFile && (
              <div data-testid="ncdb-case-detail" className="border border-nc-orange/40 bg-[#050505]/70 p-5 flex flex-col gap-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-mono text-[10px] text-nc-orange">{caseFile.case.case_number} · {caseFile.case.type}</p>
                    <p className="font-sans font-bold text-xl text-zinc-50 mt-1">{caseFile.case.title}</p>
                    <p className="font-mono text-[11px] text-zinc-400 mt-2 leading-relaxed max-w-2xl">{caseFile.case.description}</p>
                    <div className="flex gap-2 mt-3 flex-wrap items-center">
                      {caseFile.case.generated && <span data-testid="ncdb-case-generated-badge" className="font-mono text-[9px] uppercase border border-nc-blue/50 text-nc-blue px-2 py-0.5">Auto-generated</span>}
                      {caseFile.case.difficulty && <span className="font-mono text-[9px] uppercase border border-white/15 text-zinc-400 px-2 py-0.5">Difficulty {caseFile.case.difficulty}/5</span>}
                      {caseFile.case.scene && <span className="font-mono text-[9px] uppercase border border-white/15 text-zinc-400 px-2 py-0.5">{caseFile.case.scene}</span>}
                    </div>
                  </div>
                  <button data-testid="ncdb-case-close" onClick={() => setCaseFile(null)} className="font-mono text-[9px] uppercase px-2.5 py-1.5 border border-white/15 text-zinc-400 hover:border-white/40 transition-colors">Close</button>
                </div>
                {(caseFile.case.stages || []).length > 0 && (
                  <div data-testid="ncdb-case-stages">
                    <p className={`${labelCls} mb-2`}>Investigation Stages</p>
                    <div className="flex flex-col gap-1.5">
                      {caseFile.case.stages.map((s, i) => {
                        const doneBy = (caseFile.progress || []).filter((p) => (p.stages_completed || []).includes(i));
                        return (
                          <p key={i} data-testid={`ncdb-stage-${i}`} className={`font-mono text-[10px] flex items-start gap-2 flex-wrap ${doneBy.length ? "text-nc-green" : "text-zinc-300"}`}>
                            <span className="text-nc-orange shrink-0">{i + 1}.</span> {s}
                            {doneBy.length > 0 && <span className="font-mono text-[9px] uppercase text-nc-green border border-nc-green/30 px-1.5 py-0.5">Done — {doneBy.map((p) => p.player_name || p.player_id).join(", ")}</span>}
                          </p>
                        );
                      })}
                    </div>
                    {(caseFile.progress || []).length > 0 && (
                      <div className="flex gap-2 mt-3 flex-wrap" data-testid="ncdb-case-progress">
                        {caseFile.progress.map((p) => (
                          <span key={p.player_id} className="font-mono text-[9px] uppercase border border-nc-blue/40 text-nc-blue px-2 py-0.5">
                            {p.player_name || p.player_id} — {(p.stages_completed || []).length}/{caseFile.case.stages.length} stages · {(p.evidence_tested || []).length} tested
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className={`${labelCls} mb-2 flex items-center gap-1`}><Link2 className="w-3 h-3" /> Linked NPCs ({caseFile.linked_npcs.length})</p>
                    {caseFile.linked_npcs.map((n) => (
                      <button key={n.id} onClick={() => { setCaseFile(null); setTab("npcs"); openProfile(n.id); }} className="block font-mono text-[10px] text-zinc-300 hover:text-nc-blue py-1 transition-colors">▸ {n.name} <span className="text-zinc-600">({n.threat_level})</span></button>
                    ))}
                  </div>
                  <div>
                    <p className={`${labelCls} mb-2`}>Evidence Locker ({caseFile.evidence.length})</p>
                    {caseFile.evidence.map((e) => (
                      <div key={e.id} className="border-b border-white/5 py-1.5">
                        <p className="font-mono text-[10px] text-zinc-300">
                          <span className="text-nc-blue uppercase text-[9px]">{e.type}</span> — {e.description}
                          {e.tested && <span className={`font-mono text-[8px] uppercase border px-1.5 py-0.5 ml-2 ${e.lab_result === "confirmed_match" ? "text-nc-green border-nc-green/40" : e.lab_result === "no_match_dead_end" ? "text-red-400 border-red-500/40" : "text-zinc-400 border-white/20"}`}>Lab: {(e.lab_result || "tested").replace(/_/g, " ")}</span>}
                        </p>
                        {e.match_confidence != null && (
                          <div className="flex items-center gap-2 mt-1">
                            <div className="w-24 h-1 bg-white/10"><div className="h-full bg-nc-green" style={{ width: `${e.match_confidence}%` }} /></div>
                            <span className="font-mono text-[9px] text-zinc-500">{e.match_subject} {e.match_confidence}%</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className={`${labelCls} mb-2`}>Intel ({caseFile.intel.length})</p>
                    {caseFile.intel.map((n) => (
                      <div key={n.id} className="border-b border-white/5 py-1.5">
                        <p className="font-mono text-[9px] text-nc-orange">{n.ref} · {n.analyst}</p>
                        <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">{n.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {(caseFile.interviews || []).length > 0 && (
                  <div data-testid="ncdb-case-interviews">
                    <p className={`${labelCls} mb-2`}>Witness Interviews ({caseFile.interviews.length})</p>
                    <div className="flex flex-col gap-1.5">
                      {caseFile.interviews.map((iv) => (
                        <div key={iv.id} className="border border-white/10">
                          <button data-testid={`ncdb-interview-${iv.id.slice(0, 8)}`} onClick={() => setIvOpen(ivOpen === iv.id ? "" : iv.id)} className="w-full text-left px-3 py-2 flex items-center gap-3 flex-wrap hover:bg-white/5 transition-colors">
                            <span className={`font-mono text-[9px] uppercase border px-1.5 py-0.5 ${iv.status === "completed" ? "text-nc-green border-nc-green/40" : "text-nc-orange border-nc-orange/40"}`}>{iv.status}</span>
                            <span className="font-sans font-semibold text-xs text-zinc-100">{iv.npc_name}</span>
                            {iv.personality && <span className="font-mono text-[8px] uppercase border border-white/15 text-zinc-500 px-1.5 py-0.5">{iv.personality}</span>}
                            <span className="font-mono text-[9px] text-zinc-500">by {iv.player_name || iv.player_id}</span>
                            <span className="font-mono text-[9px] text-zinc-600 ml-auto">{(iv.turns || []).length} turns · {(iv.clues || []).filter((c) => c.revealed).length}/{(iv.clues || []).length} clues</span>
                          </button>
                          {ivOpen === iv.id && (
                            <div data-testid="ncdb-interview-transcript" className="border-t border-white/5 px-3 py-2 flex flex-col gap-2">
                              {iv.opening && <p className="font-mono text-[10px] text-zinc-500 italic">"{iv.opening}"</p>}
                              {(iv.turns || []).map((t) => (
                                <div key={t.n}>
                                  <p className="font-mono text-[10px] text-nc-blue">DET: {t.question}</p>
                                  <p className="font-mono text-[10px] text-zinc-300">{iv.npc_name.split(" ")[0].toUpperCase()}: {t.answer} {t.clue_revealed && <span className="text-nc-orange uppercase text-[8px] border border-nc-orange/40 px-1 py-0.5">clue revealed</span>}</p>
                                </div>
                              ))}
                              {(iv.turns || []).length === 0 && <p className="font-mono text-[10px] text-zinc-600">No questions asked yet.</p>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
